import { Form, FormikProvider, useFormik } from "formik"
import { Box, FormControlLabel, Checkbox } from "@material-ui/core"
import * as Yup from "yup"
import { useQueryClient } from "react-query"
import { toast } from "react-toastify"
import { useState, useEffect } from "react"
import FormField from "../../components/FormField"
import NCAutocomplete from "../../shared/NCAutocomplete"
import NCModal from "../../shared/NCModal"
import NCButton from "../../shared/NCButton"
import { useGetDriverList, useGetAidMonitorList } from "../../services/queries"
import API from "../../libs/api"
import { API_PATH } from "../../libs/config"

const RescueDriverModal = ({
  modalState = false,
  handleClose,
  groupId,
  currentDriverId,
  currentAidMonitorId
}) => {
  const queryClient = useQueryClient()
  const [driverSearch, setDriverSearch] = useState(null)
  const [amSearch, setAmSearch] = useState(null)

  const { data: driverData, isLoading: driversLoading } = useGetDriverList(driverSearch, 1, 100)
  const { data: amData } = useGetAidMonitorList(1, amSearch, 100, {}, { enabled: modalState })

  const driverList = driverData?.data?.data?.results || []
  const amList = amData?.data?.data?.results || []

  const driverOptions = (driverList || [])
    .filter(d => d?.user && d?.name && d.user !== currentDriverId)
    .map(d => ({ id: d.user, name: d.name }))
  const amOptions = (amList || [])
    .filter(a => a?.user && a?.name && a.user !== currentAidMonitorId)
    .map(a => ({ id: a.user, name: a.name }))

  const formik = useFormik({
    initialValues: {
      driver_id: "",
      keep_aid_monitor: true,
      aid_monitor_id: "",
      reason: ""
    },
    validationSchema: Yup.object().shape({
      driver_id: Yup.string().required("Please select a rescue driver"),
      reason: Yup.string().required("Reason is required")
    }),
    enableReinitialize: true,
    onSubmit: async (values) => {
      try {
        await API.post(API_PATH.RESCUE_GROUP(groupId), {
          driver_id: values.driver_id,
          keep_aid_monitor: values.keep_aid_monitor,
          aid_monitor_id: values.keep_aid_monitor ? null : (values.aid_monitor_id || null),
          reason: values.reason
        })
        toast.success("Rescue driver assigned successfully")
        queryClient.invalidateQueries("rideGroups")
        handleClose()
      } catch (e) {
        toast.error(e?.response?.data?.error || "Failed to assign rescue driver")
      }
    }
  })

  useEffect(() => {
    if (!modalState) {
      formik.resetForm()
      setDriverSearch(null)
      setAmSearch(null)
    }
  }, [modalState])

  return (
    <NCModal
      open={modalState}
      title="Assign Rescue Driver"
      onClose={handleClose}
      submitButton={
        <NCButton
          variant="contained"
          color="secondary"
          disableElevation
          onClick={formik.submitForm}
          style={{ width: "auto" }}
        >
          Assign Rescue Driver
        </NCButton>
      }
    >
      <FormikProvider value={formik}>
        <Form>
          <Box mt={1}>
            <FormField
              label="Select Rescue Driver"
              name="driver_id"
              as={NCAutocomplete}
              options={driverOptions}
              onChange={v => {
                if (typeof v === "number" || v) {
                  formik.setFieldValue("driver_id", v)
                } else {
                  formik.setFieldValue("driver_id", "")
                }
              }}
              onSearch={v => setDriverSearch(v || null)}
              loading={driversLoading}
              required={true}
              error={formik.errors.driver_id}
            />
          </Box>

          <Box mt={2}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formik.values.keep_aid_monitor}
                  onChange={e => formik.setFieldValue("keep_aid_monitor", e.target.checked)}
                  color="primary"
                />
              }
              label="Keep current A/M"
            />
          </Box>

          {!formik.values.keep_aid_monitor && (
            <Box mt={1}>
              <FormField
                label="Select Replacement A/M"
                name="aid_monitor_id"
                as={NCAutocomplete}
                options={amOptions}
                onChange={v => {
                  if (typeof v === "number" || v) {
                    formik.setFieldValue("aid_monitor_id", v)
                  } else {
                    formik.setFieldValue("aid_monitor_id", "")
                  }
                }}
                onSearch={v => setAmSearch(v || null)}
                error={formik.errors.aid_monitor_id}
              />
            </Box>
          )}

          <Box mt={2}>
            <FormField
              label="Reason (audit trail)"
              name="reason"
              multiline
              minRows={3}
              maxRows={6}
              required={true}
              placeholder="e.g. Driver late departure T+5 min"
              error={formik.errors.reason}
            />
          </Box>
        </Form>
      </FormikProvider>
    </NCModal>
  )
}

export default RescueDriverModal
